import moment from "moment";
import React from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { TimelineEntry, TimelineUser } from "../../models/EventTimeline";
import { JobHistoryEntry } from "../../models/JobHistory";
import {
	formatUserName,
	getTimelineContactId,
} from "../../components/Shared/EventTimeline/eventTimeline.utils";
import { denialReasonLabel } from "../../utils/job.utils";

/** i18n namespace for the job event catalogue. Kept as-is rather than renamed
 *  to `eventTimeline.job` — the 40+ existing keys are not worth touching. */
export const JOB_TIMELINE_PREFIX = "jobTimeline";

/**
 * Tailwind class for the timeline dot, keyed by event category. Explicit
 * mapping (not string building) so Tailwind's static scan picks the classes up.
 */
export const JOB_DOT_CLASS_BY_TYPE: { [type: string]: string } = {
	JOB_CREATED: "bg-blue-500",
	JOB_UPDATED: "bg-blue-400",
	JOB_REOPENED: "bg-blue-500",
	JOB_CANCELED: "bg-red-500",
	JOB_DELETED: "bg-red-500",
	JOB_DENIED: "bg-red-500",
	JOB_STATUS_CHANGED: "bg-indigo-500",
	APPLICATION_CREATED: "bg-emerald-500",
	ACCEPTED_APPLICATION_CREATED: "bg-emerald-600",
	APPLICATION_ACCEPTED: "bg-emerald-600",
	APPLICATION_DECLINED: "bg-orange-400",
	APPLICATION_CANCELLED: "bg-orange-400",
	APPLICATION_DELETED: "bg-gray-400",
	APPLICATIONS_CANCELLED_OVERLAP: "bg-orange-400",
	INVITATION_CREATED: "bg-purple-500",
	INVITATION_UPDATED: "bg-purple-400",
	INVITATION_ACCEPTED: "bg-purple-600",
	INVITATION_DECLINED: "bg-orange-400",
	INVITATION_DELETED: "bg-gray-400",
	INVITATIONS_DELETED_OVERLAP: "bg-orange-400",
	INVITATION_EXPIRED: "bg-gray-400",
	SHIFT_ADDED: "bg-amber-500",
	SHIFT_UPDATED: "bg-amber-500",
	SHIFT_REMOVED: "bg-amber-600",
};

/** Time report milestones are all one colour, whichever milestone they are. */
export const JOB_DOT_CLASS_BY_SOURCE: { [source: string]: string } = {
	TIME_REPORT: "bg-teal-500",
};

const asJobEntry = (entry: TimelineEntry) => entry as JobHistoryEntry;

/**
 * Interpolation values for the job event sentences: the consultant an
 * application/invitation/time report concerns, and the two sides of a status
 * change resolved to their Swedish status labels.
 */
export function jobLabelValues(entry: TimelineEntry, t: TFunction) {
	const job = asJobEntry(entry);

	return {
		consultant: getConsultantName(job, t),
		from: getStatusLabel(job.args?.from, t),
		to: getStatusLabel(job.args?.to, t),
	};
}

function getConsultantName(entry: JobHistoryEntry, t: TFunction): string {
	const consultant = entry.args?.consultant ?? entry.timeReport?.consultant;

	if (consultant && (consultant.firstName || consultant.lastName))
		return formatUserName(consultant, t);

	return t("jobTimeline.consultantFallback");
}

function getStatusLabel(status: unknown, t: TFunction): string {
	if (typeof status !== "string") return "";

	return t(`jobTimeline.status.${status}` as any, { defaultValue: status });
}

/** The consultant a job event concerns, for the contact context menu. */
export function jobSubjectContactId(entry: TimelineEntry): string | undefined {
	const job = asJobEntry(entry);

	return getTimelineContactId(
		(job.args?.consultant ?? job.timeReport?.consultant) as TimelineUser | undefined
	);
}

/** Shift context line for time report entries, e.g. "1 jul 08:00–16:00". */
function formatShift(shift?: { start?: string; end?: string }): string | undefined {
	if (!shift?.start || !shift?.end) return undefined;

	return `${moment(shift.start).format("D MMM HH:mm")}–${moment(shift.end).format("HH:mm")}`;
}

/**
 * Rows for the shifts a SHIFT_ADDED / SHIFT_REMOVED event concerns
 * (args.shifts), with the full date so the admin can tell which day was
 * affected: "23 jul 2026 08:00–16:00".
 */
function formatShiftRows(shifts?: { start?: string; end?: string }[]): string[] {
	return (shifts ?? [])
		.filter((shift) => shift.start && shift.end)
		.map(
			(shift) =>
				`${moment(shift.start).format("D MMM YYYY HH:mm")}–${moment(shift.end).format("HH:mm")}`
		);
}

/** Which shift a time report milestone concerns, appended to the byline. */
export function renderJobMeta(entry: TimelineEntry): React.ReactNode {
	const job = asJobEntry(entry);

	if (job.source !== "TIME_REPORT") return undefined;

	return formatShift(job.timeReport?.shift);
}

/**
 * Job-only rows under the byline: why a job was denied, and which shifts a
 * shift event concerned.
 */
const JobExtraRows: React.FC<{ entry: TimelineEntry }> = ({ entry }) => {
	const { t } = useTranslation();
	const job = asJobEntry(entry);

	// Absent on entries written before the reason was captured, or when the org
	// has no denial reasons configured
	const denialReason = denialReasonLabel(job.args?.denialReason);
	const shiftRows = job.source === "JOB" ? formatShiftRows(job.args?.shifts) : [];

	if (!denialReason && !shiftRows.length) return null;

	return (
		<>
			{denialReason && (
				<div className="mt-1.5 text-xs text-gray-600">
					{t("job.jobStatus.declinedReason", { reason: denialReason })}
				</div>
			)}

			{shiftRows.length > 0 && (
				<ul className="mt-1.5 flex flex-col gap-0.5">
					{shiftRows.map((row, i) => (
						<li key={i} className="text-xs text-gray-600">
							{row}
						</li>
					))}
				</ul>
			)}
		</>
	);
};

export function renderJobExtraRows(entry: TimelineEntry): React.ReactNode {
	return <JobExtraRows entry={entry} />;
}
